// src/oura/components/docs/SkillRunBlock.tsx
//
// The run behind a doc that a skill wrote (#3983, ourteam-backend#5268), in the
// doc's drawer: its status, who started it, when, and its cost once measured
// (never "$0" for a cost that was not taken), the note it carried, the server's
// sentence when it failed or stopped at the cap, and Re-run with a note.
//
// Re-run starts the skill again into THIS doc (#4874: no versions, so no second
// doc): its new draft replaces this one when the run ends, and a run that fails
// leaves this draft as it is. The doc the run writes is opened here.

import { useId, useState } from "react";
import { RefreshCwIcon } from "lucide-react";
import { rerunSkill, SKILL_NOTE_MAX, type SkillRun } from "@service/aeo/clientDocsWrite";
import { docsFailure } from "@service/aeo/clientDocs";
import { DrawerBlock, FOCUS_RING, LATE_TEXT, PillButton, TYPE, type DrawerFootMessage } from "@oura/ui";
import { docsFailureText } from "@oura/docs";
import { cn } from "../../lib/cn";
import { runError, runMeta, runStatus, type RunTone } from "../../docs/skillRun";

const TONE_TEXT: Record<RunTone, string> = {
    gray: "text-gray-900",
    green: "text-emerald-700",
    amber: "text-amber-700",
    rose: "text-rose-700",
};

export function SkillRunBlock({
    run,
    docId,
    canRerun,
    now,
    onBusy,
    onNote,
    onOpenDoc,
}: {
    run: SkillRun;
    docId: string;
    /** Staff on a doc they can write to. Without it the run is shown and cannot be started again. */
    canRerun: boolean;
    now: number;
    onBusy: (busy: boolean) => void;
    onNote: (message: DrawerFootMessage) => void;
    /** Open the new doc a re-run wrote into. Absent: the foot says it started and the doc is found on the board. */
    onOpenDoc?: (id: string) => void;
}) {
    const status = runStatus(run);
    const error = runError(run);
    const [open, setOpen] = useState(false);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [refused, setRefused] = useState<string | null>(null);
    const noteId = useId();
    const errorId = useId();

    const start = async () => {
        if (busy) return;
        setBusy(true);
        onBusy(true);
        setRefused(null);
        // Busy always clears: a call that throws reads as a failed one.
        let r: Awaited<ReturnType<typeof rerunSkill>>;
        try {
            r = await rerunSkill(docId, note);
        } catch {
            r = docsFailure("network");
        } finally {
            setBusy(false);
            onBusy(false);
        }
        // The server's own sentence for a refusal (not set up, the cap and the date it starts
        // again, a note too long, no skill added): shown where the button is.
        if (!r.ok) return setRefused(docsFailureText(r, "the doc"));
        setOpen(false);
        setNote("");
        onNote({ tone: "success", text: "The skill is writing a new draft. It replaces this one when the run ends." });
        if (r.data.documentId) onOpenDoc?.(r.data.documentId);
    };

    return (
        <DrawerBlock label="Skill run">
            <p className={cn("m-0 font-medium", TONE_TEXT[status.tone])}>{status.label}</p>
            <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>{runMeta(run, now)}</p>
            {run.note && <p className={cn(TYPE.tc, "m-0 mt-1 break-words text-gray-700")}>Note: {run.note}</p>}
            {/* The server's own sentence, as written. */}
            {error && <p className={cn(TYPE.tc, "m-0 mt-1 break-words text-gray-700")}>{error}</p>}

            {canRerun && run.status !== "running" && (
                <div className="mt-3">
                    {!open ? (
                        <PillButton icon={RefreshCwIcon} onClick={() => setOpen(true)}>
                            Re-run with a note
                        </PillButton>
                    ) : (
                        <>
                            <label htmlFor={noteId} className={cn(TYPE.tc, "m-0 block font-medium text-gray-900")}>
                                A note for the skill <span className="font-normal text-gray-400">(optional)</span>
                            </label>
                            <textarea
                                id={noteId}
                                value={note}
                                rows={3}
                                maxLength={SKILL_NOTE_MAX}
                                disabled={busy}
                                placeholder="Shorter answers, add the halal line"
                                aria-describedby={refused ? errorId : undefined}
                                onChange={(e) => {
                                    setNote(e.target.value);
                                    setRefused(null);
                                }}
                                className={cn(
                                    TYPE.tb,
                                    "mt-1 block w-full rounded-xl border-0 bg-gray-50 px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600",
                                    FOCUS_RING,
                                )}
                            />
                            <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-500")}>
                                {note.length} of {SKILL_NOTE_MAX}. The new draft replaces this one when the run ends.
                            </p>
                            {refused && (
                                <p id={errorId} role="alert" className={cn(TYPE.tc, LATE_TEXT, "m-0 mt-2")}>
                                    {refused}
                                </p>
                            )}
                            <div className="mt-2 flex flex-wrap gap-2">
                                <PillButton variant="primary" icon={RefreshCwIcon} busy={busy} onClick={() => void start()}>
                                    Re-run
                                </PillButton>
                                <PillButton
                                    disabled={busy}
                                    onClick={() => {
                                        setOpen(false);
                                        setNote("");
                                        setRefused(null);
                                    }}
                                >
                                    Cancel
                                </PillButton>
                            </div>
                        </>
                    )}
                </div>
            )}
        </DrawerBlock>
    );
}
